import { type PropsWithChildren, useRef } from "react";
import { ChakraProvider } from "@chakra-ui/react";
import { ThemeProvider as MaterialThemeProvider, THEME_ID } from "@mui/material/styles";
import { AppCacheProvider } from "@mui/material-nextjs/v15-pagesRouter";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { AppProps } from "next/app";
import { appWithTranslation } from "next-i18next";
import DefaultLayout from "src/components/layout/default-layout";
import { ThirdPartyScripts } from "src/components/layout/third-party-scripts";
import { WfHead } from "src/components/layout/wf-head";
import { Error404 } from "src/components/pages/error/404";
import { Error500 } from "src/components/pages/error/500";
import { ToastStack } from "src/components-mui/common/toast-stack/toast-stack";
import { useInitApp } from "src/hooks/use-init-app";
import { createNativeSyncMutationCache } from "src/lib/native-bridge/native-sync-mutation-cache";
import { useNativeWatchlistSync } from "src/lib/native-bridge/watchlist/use-native-watchlist-sync";
import { defaultQueryClientOptions } from "src/lib/tanstack-query/client.config";
import chakraTheme from "src/theme";
import materialTheme from "src/theme-mui/theme";
import { getErrorPageHeadData } from "src/utils/error-page-utils";
import { initAppHelperUtils } from "src/utils/init-app-helper-utils";
import nextI18nConfig from "../../next-i18next.config";

initAppHelperUtils();

interface IComponentByErrorPageCodeProps {
    Component: AppProps["Component"];
    pageProps: AppProps["pageProps"];
}

const ComponentByErrorPageCode = ({ Component, pageProps }: IComponentByErrorPageCodeProps) => {
    switch (pageProps.errorPageCode) {
        case 404:
            return <Error404 />;
        case 500:
            return <Error500 errorCode={pageProps.errorCode} showLogo />;
        default:
            return <Component {...pageProps} />;
    }
};

type IAppProvidersProps = PropsWithChildren<AppProps>;

const AppProviders = (props: IAppProvidersProps) => {
    const { children } = props;

    const queryClientRef = useRef<QueryClient | null>(null);
    if (!queryClientRef.current) {
        queryClientRef.current = new QueryClient({ defaultOptions: defaultQueryClientOptions, mutationCache: createNativeSyncMutationCache() });
    }

    return (
        <QueryClientProvider client={queryClientRef.current}>
            <ChakraProvider theme={chakraTheme} resetCSS>
                <AppCacheProvider {...props}>
                    <MaterialThemeProvider theme={{ [THEME_ID]: materialTheme }}>
                        <ToastStack />
                        {children}
                    </MaterialThemeProvider>
                </AppCacheProvider>
            </ChakraProvider>
        </QueryClientProvider>
    );
};

const AppInit = ({ pageProps }: Pick<AppProps, "pageProps">) => {
    useInitApp(pageProps);
    useNativeWatchlistSync();

    return null;
};

function App(props: AppProps) {
    const { Component, pageProps } = props;

    const headProps = pageProps.head ?? (pageProps.errorPageCode ? getErrorPageHeadData(pageProps.errorPageCode) : undefined);

    return (
        <AppProviders {...props}>
            <AppInit pageProps={pageProps} />
            <WfHead {...headProps} />
            <ThirdPartyScripts />
            <DefaultLayout {...pageProps}>
                <ComponentByErrorPageCode Component={Component} pageProps={pageProps} />
            </DefaultLayout>
        </AppProviders>
    );
}

export default appWithTranslation(App, nextI18nConfig);
